import { useEffect, useState } from 'react';
import EmailConfirmation, { emailsMatch } from './EmailConfirmation';

const emailKinds = ['confirmation', 'invoice', 'receipt'];

export default function OrderEmails({
    api, applyOrder, busy, order, orderId, run, setMessage,
}) {
    const [emailReview, setEmailReview] = useState(null);
    const [emailError, setEmailError] = useState('');
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const savedReceiptEmail = order.emails.receipt.recipient || order.billingEmail;
    const [receiptEmail, setReceiptEmail] = useState(
        order.emails.receipt.recipient || order.billingEmail,
    );
    const needsEmailConfirmation = emailReview === 'receipt'
        && !emailsMatch(receiptEmail, savedReceiptEmail);
    const emailConfirmed = !needsEmailConfirmation || emailsMatch(receiptEmail, emailConfirmation);

    useEffect(() => {
        setReceiptEmail(order.emails.receipt.recipient || order.billingEmail);
        setEmailConfirmation('');
    }, [order.billingEmail, order.emails.receipt.recipient]);

    const sendEmail = (event) => {
        event.preventDefault();
        if (!emailConfirmed) return;
        setEmailError('');
        run(async () => {
            const kind = emailReview;
            try {
                const data = await api(`/orders/${orderId}/emails/${kind}`, {
                    method: 'POST',
                    body: { recipient: receiptEmail, confirmDuplicate: true },
                });
                applyOrder(data);
                setEmailReview(null);
                setMessage('Email sent.');
            } catch (requestError) {
                // A failed response may still have recorded a send. Preserve that original error.
                try {
                    applyOrder(await api(`/orders/${orderId}`));
                } catch {
                    // Keep the last known status if refreshing also fails.
                }
                throw requestError;
            }
        }, { reportError: false }).then((requestError) => {
            if (requestError) setEmailError(requestError.message);
        });
    };

    return (
        <div className="po-panel">
            <h3>Email history</h3>
            {emailError && (
                <p
                    className="po-error"
                    role="alert">
                    {emailError}
                </p>
            )}
            <ul className="po-email-list">
                {emailKinds.map((kind) => {
                    const sent = order.emails[kind];
                    let label = 'Not sent';
                    if (sent.status === 'sent') label = 'Email sent';
                    if (sent.status === 'sending') label = 'Pending';
                    if (sent.status === 'unknown') label = 'Unconfirmed - check inbox';
                    return (
                        <li key={kind}>
                            <div>
                                <strong>{kind}</strong>
                                <span>
                                    {label}
                                    {sent.recipient ? ` · ${sent.recipient}` : ''}
                                    {sent.sentAt
                                        ? ` · ${new Date(sent.sentAt).toLocaleString()}`
                                        : ''}
                                </span>
                            </div>
                            <button
                                type="button"
                                disabled={
                                    busy
                                    || (kind === 'receipt' && order.paymentStatus !== 'paid')
                                }
                                onClick={() => {
                                    setEmailError('');
                                    setEmailReview(kind);
                                }}>
                                {sent.status === 'unsent' ? 'Send' : 'Resend'}
                            </button>
                        </li>
                    );
                })}
            </ul>
            {emailReview && (
                <form
                    onSubmit={sendEmail}
                    className="po-review">
                    <h4>{`Send ${emailReview}`}</h4>
                    {emailReview === 'receipt' ? (
                        <>
                            <label
                                htmlFor="ordereditor-field-2"
                                className="po-field">
                                <span>Receipt recipient</span>
                                <input
                                    id="ordereditor-field-2"
                                    type="email"
                                    required
                                    maxLength={254}
                                    value={receiptEmail}
                                    onChange={(event) => setReceiptEmail(event.target.value)} />
                            </label>
                            {needsEmailConfirmation && (
                                <EmailConfirmation
                                    label="Confirm receipt recipient email"
                                    email={receiptEmail}
                                    value={emailConfirmation}
                                    onChange={setEmailConfirmation} />
                            )}
                        </>
                    ) : (
                        <p>
                            Recipient:
                            {' '}
                            {emailReview === 'invoice'
                                ? order.billingEmail
                                : order.customerEmail}
                        </p>
                    )}
                    <p>
                        This sends a real email using the existing order and code.
                        If already sent, the recipient will receive another copy.
                    </p>
                    <div className="po-actions">
                        <button
                            type="submit"
                            disabled={busy || !emailConfirmed}>
                            Confirm send
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                                setEmailError('');
                                setEmailReview(null);
                            }}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
